// Excel-Upload der Mailing-Liste (DCP-2228 Folge-Feature) — Zentrale und Admin
//
// Sitzt auf der Mailing-Pflegeseite, nicht mehr in den Einstellungen: dort steht seit
// der Gruppierung nur noch eine Verweiszeile, und ein Hochladeknopf daneben wäre ein
// zweiter Bedienweg für dasselbe Thema.
//
// Ersetzt den gesamten Bestand mit dem Inhalt der Datei. Der Server erzwingt dieselbe
// Beschränkung (createRequireCentral); dieses Steuerelement zu verbergen ist Bequemlich-
// keit, nicht die Kontrolle.
//
// Das Ergebnis wird vollständig gemeldet — importiert, entfernt, übersprungen und die
// Fehler je Zeile — statt eines nackten „hat geklappt". Ein Import, der stillschweigend
// Zeilen verliert, macht Veranstaltungen unbewerbbar, ohne einen Hinweis zu geben.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useImportMailings } from '@/services/event-service';
import { ApiError } from '@/lib/api-client';
import type { MailingImportResult } from '@/types';

export function MailingImportControl() {
  const { t } = useTranslation('settings');
  const inputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<MailingImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const importMutation = useImportMailings();

  const handleFile = async (file: File | undefined) => {
    if (!file) return;

    setResult(null);
    setError(null);

    try {
      setResult(await importMutation.mutateAsync(file));
    } catch (err) {
      // The backend phrases these in German and they are actionable ("columns not
      // found", "no valid row"), so show them verbatim rather than a generic text.
      setError(err instanceof ApiError ? err.message : t('mailings.uploadFailed'));
    } finally {
      // Clear the input so selecting the same file again re-triggers a change.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div>
        <p className="text-sm font-medium">{t('mailings.title')}</p>
        <p className="text-xs text-muted-foreground">{t('mailings.description')}</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />

      <Button
        type="button"
        variant="outline"
        className="gap-2"
        disabled={importMutation.isPending}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4" />
        {importMutation.isPending ? t('mailings.uploading') : t('mailings.upload')}
      </Button>

      {/* Der Warnkasten zur Ersetzen-Semantik steht auf der Seite selbst, dauerhaft
          sichtbar — hier wäre er doppelt. */}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {result && (
        <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
          <p>{t('mailings.imported', { count: result.imported })}</p>
          {result.removed > 0 && <p>{t('mailings.removed', { count: result.removed })}</p>}
          {result.skipped > 0 && <p>{t('mailings.skipped', { count: result.skipped })}</p>}

          {result.errors.length > 0 && (
            <div className="pt-1">
              <p className="font-medium text-amber-700">
                {t('mailings.rowErrors', { count: result.errors.length })}
              </p>
              <ul className="list-inside list-disc text-xs text-amber-700">
                {result.errors.map((rowError) => (
                  <li key={`${rowError.row}-${rowError.reason}`}>
                    {t('mailings.row', { row: rowError.row })}: {rowError.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
